/**
 * 国旗组件
 * 优先使用 flagImg 图片（pub_assets/flags 目录），无图片或加载失败时使用 flag emoji
 * 支持 countryCode（如 "CN"）或国家名称（如 "China"）
 * 兼容：台湾使用中国国旗显示
 */

import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import useCountryInfoStore from '@/stores/country-info';
import type { CountryFlagProps } from './types';

// /** 使用 BASE_URL 确保 dev/prod/test 环境路径正确（prod/test 走 CDN） */
// const FLAGS_BASE = `${import.meta.env.BASE_URL || '/'}pub_assets/flags`;
const FLAGS_BASE = `/pub_assets/flags`;

/** 台湾相关标识，用于兼容显示中国国旗 */
const TAIWAN_CODES = ['TW'];
const TAIWAN_NAMES = ['taiwan', '台湾', '中國台灣', '中国台湾'];

/** 中国国旗兜底（当国家列表中无 CN 时使用） */
const CHINA_FLAG_FALLBACK = {
  flagEmoji: '🇨🇳',
  flagImg: 'China',
  enName: 'China',
  name: '中国',
  code: 'CN',
};

function isTaiwan(countryCode?: string, country?: { code?: string; name?: string; enName?: string }): boolean {
  if (!countryCode && !country) return false;
  const code = (country?.code ?? countryCode ?? '').toUpperCase();
  const name = (country?.name ?? country?.enName ?? countryCode ?? '').toLowerCase();
  if (TAIWAN_CODES.includes(code)) return true;
  return TAIWAN_NAMES.some(n => name.includes(n.toLowerCase()));
}

/** 根据 flagImg 或 enName 生成图片路径 */
function getFlagImageSrc(flagImg: string, enName?: string): string | undefined {
  if (flagImg?.startsWith('http')) return flagImg;
  if (flagImg?.trim()) {
    const name = flagImg.trim();
    return `${FLAGS_BASE}/${name}${name.endsWith('.png') ? '' : '.png'}`;
  }
  if (enName?.trim()) {
    const name = enName.trim().replace(/\s+/g, '_');
    return `${FLAGS_BASE}/${name}.png`;
  }
  return undefined;
}

export const CountryFlag: FC<CountryFlagProps> = ({ countryCode, size = 16, className, rounded = false }) => {
  const { countryList, getCountryByCodeOrName, getCountryListWithCache } = useCountryInfoStore();
  const rawCountry = getCountryByCodeOrName(countryCode ?? '');
  // 台湾兼容：使用中国国旗（支持 code=TW 或名称如 Taiwan、台湾 等）
  const isTaiwanRegion =
    countryCode?.toUpperCase() === 'TW' ||
    TAIWAN_NAMES.some(n => (countryCode ?? '').toLowerCase().includes(n.toLowerCase())) ||
    (rawCountry ? isTaiwan(countryCode ?? '', rawCountry) : false);
  const country = isTaiwanRegion ? (getCountryByCodeOrName('CN') ?? CHINA_FLAG_FALLBACK) : rawCountry;

  // 若列表为空，尝试拉取（如 layout 未提前加载）
  useEffect(() => {
    if (countryCode && countryList.length === 0) {
      getCountryListWithCache();
    }
  }, [countryCode, countryList.length, getCountryListWithCache]);

  const flagImgSrc = country ? getFlagImageSrc(country.flagImg, country.enName) : undefined;
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [flagImgSrc]);

  // 优先使用图片，有图片且未加载失败时用图片
  if (flagImgSrc && country && !imgError) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center overflow-hidden flex-shrink-0 bg-gray-100',
          rounded && 'rounded-full',
          className
        )}
        style={{
          width: size,
          height: rounded ? size : size * 0.67,
        }}
      >
        <img
          src={flagImgSrc}
          alt={country.name || country.enName || countryCode || ''}
          className={cn('object-cover w-full h-full', rounded && 'rounded-full')}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      </span>
    );
  }

  // 无图片或图片加载失败时，有 emoji 则用 emoji
  if (country?.flagEmoji) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center overflow-hidden flex-shrink-0',
          rounded && 'rounded-full',
          className
        )}
        style={{
          width: size,
          height: rounded ? size : size * 0.67,
          fontSize: size * 0.9,
          lineHeight: 1,
        }}
      >
        {country.flagEmoji}
      </span>
    );
  }

  // 都无时显示国家代码
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center bg-gray-200 text-gray-500 text-xs',
        rounded && 'rounded-full',
        className
      )}
      style={{ width: size, height: size * 0.67 }}
    >
      {countryCode?.slice(0, 2).toUpperCase()}
    </span>
  );
};

export type { CountryFlagProps } from './types';
